import { useMutation, useQuery } from '@apollo/client';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_UPERCUT, GET_UPERCUTS, UPDATE_UPERCUT } from '@gql/upercut';
import { GET_UPERCUT_ROLES } from '@gql/upercut-permissions';
import { Box, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import UpercutForm from '../../modules/upercut/UpercutForm';
import { UpercutUpdateFormValues } from '../../modules/upercut/types';

interface RoleOption {
    _id: string;
    name: string;
}

const EditUpercutPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const formMethods = useForm<UpercutUpdateFormValues>({
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            passwordConfirm: '',
            roleId: '',
        },
    });

    const { data: upercutData, loading: loadingUpercut } = useQuery(GET_UPERCUT, {
        variables: { id },
        skip: !id,
        fetchPolicy: 'cache-and-network',
    });

    const [updateUpercut, { loading: isUpdating }] = useMutation(UPDATE_UPERCUT);

    const { data: rolesData } = useQuery(GET_UPERCUT_ROLES, {
        fetchPolicy: 'cache-and-network',
    });
    const roles: RoleOption[] = rolesData?.upercutRoles ?? [];

    useEffect(() => {
        const upercut = upercutData?.getUpercut;
        if (!upercut) return;
        const currentRoleId = upercut.assignedRole?.role?._id ?? '';
        formMethods.reset({
            firstName: upercut.firstName ?? '',
            lastName: upercut.lastName ?? '',
            email: upercut.email ?? '',
            password: '',
            passwordConfirm: '',
            roleId: currentRoleId,
        });
    }, [upercutData, formMethods]);

    const handleSubmit = async (values: UpercutUpdateFormValues) => {
        if (!id) return;
        try {
            const updateInput: any = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
            };
            if (values.password) {
                updateInput.password = values.password;
                updateInput.passwordConfirm = values.passwordConfirm;
            }
            if (values.roleId !== undefined) {
                updateInput.roleId = values.roleId;
            }
            await updateUpercut({ variables: { id, upercutUpdateInput: updateInput },
                refetchQueries: [
                    { query: GET_UPERCUTS, variables: { limit: 10, offset: 0, filter: null } },
                ],
                awaitRefetchQueries: true,
            });
            showSnackbar('Utilisateur Upercut mis à jour avec succès.', 'success');
            navigate('/upercuts');
        } catch (err: any) {
            showSnackbar(err?.message || "Échec de la mise à jour de l'utilisateur.", 'error');
        }
    };

    if (loadingUpercut) {
        return (
            <Box p={3} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <UpercutForm
                formMode="edit"
                formMethods={formMethods as any}
                onSubmit={handleSubmit as any}
                onCancel={() => navigate('/upercuts')}
                isSubmitting={isUpdating}
                roles={roles}
            />
        </Box>
    );
};

export default EditUpercutPage;
